import * as React from 'react';
import { Logo } from './Logo';
import { cn } from '../utils/cn';

export interface SidebarItem {
  label: string;
  href: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  active?: boolean;
}

export interface SidebarSection {
  title?: string;
  items: SidebarItem[];
}

export interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  brandName?: string;
  brandHref?: string;
  sections: SidebarSection[];
  footer?: React.ReactNode;
}

export function Sidebar({
  brandName,
  brandHref = '/',
  sections,
  footer,
  className,
  ...props
}: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-screen w-64 shrink-0 flex-col border-r border-[var(--color-border)] bg-white',
        className,
      )}
      {...props}
    >
      <div className="px-6 py-5">
        <a href={brandHref} className="inline-flex">
          <Logo brandName={brandName} />
        </a>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-6">
        {sections.map((section, idx) => (
          <div key={idx} className={cn('mb-6', idx > 0 && 'mt-6')}>
            {section.title ? (
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
                {section.title}
              </p>
            ) : null}
            <ul className="space-y-1">
              {section.items.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className={cn(
                      'group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                      item.active
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-foreground)]',
                    )}
                  >
                    {item.icon ? <span className="size-4 shrink-0">{item.icon}</span> : null}
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge ? <span>{item.badge}</span> : null}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {footer ? (
        <div className="border-t border-[var(--color-border)] p-4">{footer}</div>
      ) : null}
    </aside>
  );
}
